import { Component, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';
import { PrimeInvoiceSidebarGeneralComponent } from '@app/framework/interaction-bar/prime-commitments/prime-invoice-sidebar/prime-invoice-sidebar-general/prime-invoice-sidebar-general.component';
import { PrimeInvoiceSidebarDetailComponent } from '@app/framework/interaction-bar/prime-commitments/prime-invoice-sidebar/prime-invoice-sidebar-detail/prime-invoice-sidebar-detail.component';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import {
  INTERACTION_BAR_STATES,
  INTERACTION_BAR_WIDENESS,
} from '@app/framework/constants/interaction-bar.constants';
import { PrimeInvoiceStore } from '@app/store/prime-invoice/primeInvoice.store';
import { NotificationsService } from '@app/services/notifications.service';
import { PageLoadingComponent } from '@app/framework/page-loading/page-loading.component';
import { CheckboxComponent } from '@app/framework/inputs/checkbox/checkbox.component';
import { CheckboxChangeEvent } from '@app/framework/interfaces/input.type';

@Component({
  selector: 'app-prime-invoice-sidebar',
  imports: [
    SidebarHeaderComponent,
    PrimeInvoiceSidebarGeneralComponent,
    PrimeInvoiceSidebarDetailComponent,
    SimpleButtonComponent,
    PageLoadingComponent,
    FormsModule,
    CheckboxComponent,
  ],
  templateUrl: './prime-invoice-sidebar.component.html',
  styleUrl: './prime-invoice-sidebar.component.scss',
})
export class PrimeInvoiceSidebarComponent implements OnInit, OnDestroy {
  interactionBarService = inject(InteractionBarStateService);
  primeInvoiceStore = inject(PrimeInvoiceStore);
  store = inject(Store);
  notif = inject(NotificationsService);

  isEdit = input<boolean>(false);
  invoiceId = input<number | null>(null);
  currentPage = signal(1);
  canBeEdited = this.primeInvoiceStore.canBeEdited;
  isLoading = this.primeInvoiceStore.isLoading;
  model = this.primeInvoiceStore.state.model;

  isDestroyed$ = new Subject<boolean>();
  hideZeroDollarSov = false;

  ngOnInit() {
    this.primeInvoiceStore.clearState();
    if (this.isEdit()) {
      if (this.invoiceId()) {
        this.primeInvoiceStore.loadPrimeInvoice(this.invoiceId());
      } else {
        this.notif.showError('Could not load invoice data for edit.');
      }
    } else {
      this.primeInvoiceStore.loadSOVData();
    }

    this.interactionBarService.setDisableClose(true);
    this.interactionBarService.announceState
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((state) => {
        if (
          [INTERACTION_BAR_STATES.CLOSE, INTERACTION_BAR_STATES.SUCCESS_CLOSED].includes(
            state.action,
          )
        ) {
          this.primeInvoiceStore.clearState();
        }
      });
  }

  goToPreviousPage() {
    if (this.currentPage() <= 1) {
      return;
    }
    this.interactionBarService.setWideness(INTERACTION_BAR_WIDENESS.NORMAL);
    this.currentPage.update((page) => page - 1);
  }

  goToNextPage() {
    if (this.currentPage() >= 2) {
      return;
    }
    this.primeInvoiceStore.firstPageForm().form.markAllAsTouched();
    if (this.primeInvoiceStore.firstPageForm().form.invalid) {
      return;
    }

    this.interactionBarService.setWideness(INTERACTION_BAR_WIDENESS.WIDER);
    this.currentPage.update((page) => page + 1);
  }

  save() {
    if (this.isEdit()) {
      this.primeInvoiceStore.updateInvoice();
    } else {
      this.primeInvoiceStore.createInvoice();
    }
  }
  ngOnDestroy() {
    this.interactionBarService.setWideness(INTERACTION_BAR_WIDENESS.NORMAL);
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  close() {
    this.interactionBarService.close();
  }

  onHideZeroDollarSovChange(event: CheckboxChangeEvent) {
    this.primeInvoiceStore.setHideZeroDollarSov(event.checked);
  }
}
